import React from "react";
import { Card, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSidebar } from "../../ui/sidebar";

type RiskMatrixSkeletonProps = {
  isMobile?: boolean;
};

export function RiskMatrixSkeleton({
  isMobile = false,
}: RiskMatrixSkeletonProps) {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  const maxWidth = isCollapsed
    ? "max-w-[calc(100vw-148px)]"
    : "max-w-[calc(100vw-356px)]";
  if (isMobile) {
    return (
      <div className="space-y-4">
        {/* Navigation Section */}
        <div className="flex justify-end items-center gap-2">
          <Skeleton className="h-10 w-10 rounded" />
          <Skeleton className="h-5 w-12" />
          <Skeleton className="h-10 w-10 rounded" />
        </div>

        {/* Header Card with Table */}
        <Card>
          <CardHeader className="p-2">
            <table className="w-full border-collapse border border-black border-2">
              <thead>
                {/* Row 1 */}
                <tr>
                  <th className="bg-blue-300 font-bold text-xs border border-black px-2 py-1 text-left">
                    <Skeleton className="h-4 w-20" />
                  </th>
                  <th className="bg-blue-300 font-bold text-center text-sm border border-black px-2 py-1">
                    <Skeleton className="h-4 w-4 mx-auto" />
                  </th>
                  <th className="bg-blue-300 font-bold text-xs border border-black px-2 py-1 text-center">
                    <Skeleton className="h-4 w-16" />
                  </th>
                </tr>

                {/* Row 2 */}
                <tr>
                  <th className="bg-blue-700 text-white font-bold text-sm border border-black px-2 py-1 text-left">
                    <Skeleton className="h-4 w-24 bg-blue-400" />
                  </th>
                  <th className="bg-blue-700 text-white italic font-bold text-sm border border-black px-2 py-1 text-center">
                    <Skeleton className="h-4 w-16 bg-blue-400" />
                  </th>
                  <th className="bg-blue-300 font-bold text-xs border border-black px-2 py-1 text-center">
                    <Skeleton className="h-4 w-12" />
                  </th>
                </tr>

                {/* Row 3 */}
                <tr>
                  <th className="bg-blue-300 font-bold text-sm border border-black px-2 py-1 text-left">
                    <Skeleton className="h-4 w-32" />
                  </th>
                  <th className="text-center font-bold text-sm border border-black px-2 py-1">
                    <Skeleton className="h-4 w-4 mx-auto" />
                  </th>
                  <th className="border border-black p-0">
                    <Skeleton className="h-10 w-full rounded-none" />
                  </th>
                </tr>
              </thead>
            </table>
          </CardHeader>
        </Card>

        {/* Actor Cards */}
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card className="p-2" key={i}>
              <table className="w-full border-collapse border border-black border-2">
                <thead>
                  {/* Row 1: Competence Level */}
                  <tr>
                    <th
                      colSpan={2}
                      className="border border-black [border-bottom-style:dashed] px-2 py-1 text-center text-xs font-semibold italic bg-white"
                    >
                      <Skeleton className="h-4 w-24 mx-auto" />
                    </th>
                  </tr>

                  {/* Row 2: Actor Name */}
                  <tr>
                    <th
                      colSpan={2}
                      className="border border-black [border-top-style:dashed] px-2 py-1 text-center text-xs font-bold bg-white"
                    >
                      <Skeleton className="h-4 w-32 mx-auto" />
                    </th>
                  </tr>

                  {/* Row 3: Column Headers */}
                  <tr>
                    <th className="bg-blue-300 font-bold text-xs border border-black px-2 py-1 text-center">
                      <Skeleton className="h-4 w-16 mx-auto" />
                    </th>
                    <th className="bg-blue-300 font-bold text-xs border border-black px-2 py-1 text-center">
                      <Skeleton className="h-4 w-20 mx-auto" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Row 4: Data Cells */}
                  <tr>
                    <td className="border border-black p-0">
                      <Skeleton className="h-10 w-full rounded-none" />
                    </td>
                    <td className="border border-black px-2 py-1 text-center font-medium">
                      <Skeleton className="h-4 w-12 mx-auto" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
          ))}
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center gap-2 pt-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-2 w-2 rounded-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex gap-0 w-full overflow-x-auto mb-14 ${maxWidth}`}>
      {/* Main Risk Matrix Table */}
      <table className="w-full border-collapse border border-black border-2">
        {/* Header - Three rows */}
        <thead className="sticky top-0 z-10">
          {/* Row 1: Sector name and competence categories */}
          <tr className="bg-white">
            <th
              colSpan={2}
              className="border border-black border-1 px-2 py-2 text-left bg-blue-100"
            >
              <Skeleton className="h-6 w-40" />
            </th>
            <th
              rowSpan={2}
              className="border-t border-l border-b-2 border-r border-black [border-right-style:dashed] px-2 py-1 text-center bg-white"
            >
              <Skeleton className="h-4 w-20" />
            </th>
            {Array.from({ length: 5 }).map((_, idx) => (
              <th
                key={idx}
                colSpan={2}
                className="border border-black [border-left-style:dashed] [border-bottom-style:dashed] px-2 py-1 text-center"
              >
                <Skeleton className="h-4 w-32" />
              </th>
            ))}
          </tr>

          {/* Row 2: Dimensiunea entității and actor names */}
          <tr className="bg-white">
            <th className="border border-black border-2 px-2 py-1 text-left bg-blue-300">
              <Skeleton className="h-8 w-36" />
            </th>
            <th className="border border-black border-2 px-2 py-1 text-center bg-blue-300">
              <Skeleton className="h-5 w-6" />
            </th>
            {Array.from({ length: 5 }).map((_, idx) => (
              <th
                key={idx}
                colSpan={2}
                className="border border-black [border-left-style:dashed] [border-top-style:dashed] border-b-2 px-2 py-1 text-center"
              >
                <Skeleton className="h-4 w-24" />
              </th>
            ))}
          </tr>

          {/* Row 3: Column headers */}
          <tr className="bg-slate-50 dark:bg-slate-600">
            <th className="border border-black border-2 px-2 py-1 bg-blue-700 min-w-[320px]">
              <Skeleton className="h-4 w-48 bg-blue-400" />
            </th>
            <th className="border border-black border-2 px-2 bg-blue-700 min-w-[85px]">
              <Skeleton className="h-8 w-16 bg-blue-400" />
            </th>
            <th className="border border-black border-2 px-2 py-1 bg-blue-300">
              <Skeleton className="h-4 w-16" />
            </th>
            {Array.from({ length: 5 }).map((_, idx) => (
              <React.Fragment key={idx}>
                <th className="border border-black border-2 px-1 py-1 bg-blue-300">
                  <Skeleton className="h-4 w-20" />
                </th>
                <th className="border border-black border-2 px-1 py-1 bg-blue-300">
                  <Skeleton className="h-8 w-16" />
                </th>
              </React.Fragment>
            ))}
          </tr>
        </thead>

        {/* Body */}
        <tbody>
          {Array.from({ length: 5 }).map((_, catIdx) => (
            <tr key={catIdx} className="bg-white">
              <td className="border border-black border-dashed px-2 bg-blue-300">
                <Skeleton className="h-4 w-56" />
              </td>
              <td className="border border-black border-dashed px-2 text-center">
                <Skeleton className="h-4 w-4 mx-auto" />
              </td>
              <td className="border border-dashed border-black p-0">
                <Skeleton className="h-10 w-full rounded-none" />
              </td>
              {Array.from({ length: 5 }).map((_, actorIdx) => (
                <React.Fragment key={actorIdx}>
                  <td className="border border-black border-dashed">
                    <Skeleton className="h-10 w-full rounded-none" />
                  </td>
                  <td className="border border-black border-dashed text-center">
                    <Skeleton className="h-4 w-12 mx-auto" />
                  </td>
                </React.Fragment>
              ))}
            </tr>
          ))}
        </tbody>

        {/* Footer with totals */}
        <tfoot className="bg-white sticky bottom-0 z-10 font-bold">
          <tr>
            <td colSpan={4} className="border border-black px-2 py-2 text-left">
              <Skeleton className="h-6 w-20" />
            </td>
            {Array.from({ length: 5 }).map((_, idx) => (
              <React.Fragment key={idx}>
                {idx === 0 ? (
                  <td className="border border-black px-2 text-center">
                    <Skeleton className="h-5 w-12 mx-auto" />
                  </td>
                ) : (
                  <>
                    <td className="border border-black"></td>
                    <td className="border border-black px-2 text-center">
                      <Skeleton className="h-5 w-12 mx-auto" />
                    </td>
                  </>
                )}
              </React.Fragment>
            ))}
          </tr>
        </tfoot>
      </table>

      {/* Score/Category Table */}
      <table className="border-collapse border-2 border-l-0 border-black self-end">
        <thead>
          <tr>
            <th className="border border-black border-l-1 px-4 bg-blue-200 text-center">
              <Skeleton className="h-4 w-10" />
            </th>
            <th className="border border-black px-4 bg-blue-200 text-center min-w-[100px]">
              <Skeleton className="h-8 w-14 mx-auto" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-black px-4 py-2 text-center">
              <Skeleton className="h-6 w-12 mx-auto" />
            </td>
            <td className="border border-black px-4 text-center bg-green-500">
              <Skeleton className="h-5 w-14 mx-auto bg-green-300" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
